/* Collection grid: live Shopify filters + sort (Section Rendering API), load more, "fits my wall" and "narrowest first" (client side). */
(() => {
  if (customElements.get('nt-collection')) return;

  class NtCollection extends HTMLElement {
    connectedCallback() {
      this.filtersOpen = false;     // phones/tablets: filters drop-down panel
      this.wall = null;             // null = any width
      this.narrow = false;          // client-side sort by width
      this.serverSort = null;       // sort to keep while "narrow" is selected
      const params = new URLSearchParams(location.search);
      const w = parseInt(params.get('wall'), 10);
      if (w) this.wall = w;
      if (params.get('sort') === 'narrow') this.narrow = true;

      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('input', (e) => { if (e.target.matches('[data-nt-col-wallinput]')) this.onWall(e.target); });
      this.addEventListener('submit', (e) => { e.preventDefault(); this.refresh(); });
      this.addEventListener('click', (e) => this.onClick(e));
      this.hydrate();
      /* opened with ?wall= or ?sort=narrow (shared link, back button): the filter needs every product */
      if (this.wall || this.narrow) this.ensureAll().then(() => this.apply());
    }

    q(sel) { return this.querySelector(sel); }
    form() { return this.q('[data-nt-col-form]'); }

    /* After first paint or a re-render: restore client-side state. */
    hydrate() {
      this.paintToggle();
      const input = this.q('[data-nt-col-wallinput]');
      if (input) {
        if (this.wall) input.value = Math.min(this.wall, +input.max);
        this.paintWall(input);
      }
      const sort = this.q('[data-nt-col-sort]');
      if (sort && this.narrow && sort.querySelector('option[value="narrow"]')) sort.value = 'narrow';
      this.apply();
    }

    onChange(e) {
      const t = e.target;
      if (t.matches('[data-nt-col-wallinput]')) { this.commitWall(); return; }
      if (t.matches('[data-nt-col-sort]')) {
        if (t.value === 'narrow') { this.narrow = true; this.ensureAll().then(() => { this.apply(); this.syncUrl(); }); return; }
        this.narrow = false;
      }
      if (t.closest('[data-nt-col-form]') || t.matches('[data-nt-col-sort]')) {
        if (t.type === 'checkbox') { const l = t.closest('label'); if (l) l.classList.toggle('is-on', t.checked); }
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.refresh(), t.type === 'number' ? 600 : 120);
      }
    }

    paintToggle() {
      this.classList.toggle('is-filters-open', this.filtersOpen);
      const t = this.q('[data-nt-col-ftoggle]');
      if (t) t.setAttribute('aria-expanded', String(this.filtersOpen));
    }

    onClick(e) {
      const toggle = e.target.closest('[data-nt-col-ftoggle]');
      if (toggle) { this.filtersOpen = !this.filtersOpen; this.paintToggle(); return; }
      const clear = e.target.closest('[data-nt-col-clear]');
      if (clear) { e.preventDefault(); this.wall = null; this.narrow = false; this.refresh(this.dataset.url); return; }
      const link = e.target.closest('[data-nt-col-link]');
      if (link) { e.preventDefault(); this.refresh(link.href); return; }
      const wallPill = e.target.closest('[data-nt-col-wallpill]');
      if (wallPill) { e.preventDefault(); this.setWall(null); return; }
      const more = e.target.closest('[data-nt-col-more]');
      if (more) { e.preventDefault(); this.loadMore(more); }
    }

    /* ---------- server: filters, sort, pages ---------- */
    buildUrl() {
      const form = this.form();
      const fd = form ? new FormData(form) : new FormData();
      const sort = this.q('[data-nt-col-sort]');
      if (!form && sort) fd.set('sort_by', sort.value);
      fd.delete('wall');
      if (fd.get('sort_by') === 'narrow') fd.delete('sort_by');
      const p = new URLSearchParams();
      for (const [k, v] of fd.entries()) { if (v !== '') p.append(k, v); }
      const qs = p.toString();
      return this.dataset.url + (qs ? '?' + qs : '');
    }

    sectionUrl(url) {
      const u = new URL(url, location.origin);
      u.searchParams.set('section_id', this.dataset.sectionId);
      return u.toString();
    }

    async fetchDoc(url) {
      const res = await fetch(this.sectionUrl(url), { credentials: 'same-origin' });
      const html = await res.text();
      return new DOMParser().parseFromString(html, 'text/html');
    }

    async refresh(url) {
      url = url || this.buildUrl();
      this.classList.add('is-loading');
      try {
        const doc = await this.fetchDoc(url);
        const fresh = doc.querySelector('nt-collection');
        if (!fresh) { location.href = url; return; }
        this.innerHTML = fresh.innerHTML;
        this._all = null;
        this.dataset.total = fresh.dataset.total;
        this.baseUrl = url;
        if (this.wall || this.narrow) await this.ensureAll();
        this.hydrate();
        this.syncUrl(url);
      } catch (err) { location.href = url; }
      finally { this.classList.remove('is-loading'); }
    }

    async loadMore(btn) {
      btn.classList.add('is-loading');
      try {
        const doc = await this.fetchDoc(btn.href);
        this.appendFrom(doc);
      } finally { btn.classList.remove('is-loading'); }
      this.apply();
    }

    appendFrom(doc) {
      const grid = this.q('[data-nt-col-grid]');
      const items = doc.querySelectorAll('[data-nt-col-grid] > li');
      if (grid) items.forEach((li) => grid.appendChild(document.importNode(li, true)));
      const nextBtn = doc.querySelector('[data-nt-col-more]');
      const btn = this.q('[data-nt-col-more]');
      if (btn) { if (nextBtn) btn.href = nextBtn.href; else btn.remove(); }
    }

    /* Wall filter and narrow sort need every product of the (filtered) collection on the page. */
    ensureAll() {
      if (!this._all) this._all = this.loadAll().catch(() => { this._all = null; });
      return this._all;
    }

    async loadAll() {
      let btn = this.q('[data-nt-col-more]');
      let guard = 0;
      while (btn && guard++ < 20) {
        const doc = await this.fetchDoc(btn.href);
        this.appendFrom(doc);
        btn = this.q('[data-nt-col-more]');
      }
    }

    syncUrl(url) {
      const u = new URL(url || this.baseUrl || location.href, location.origin);
      u.searchParams.delete('section_id');
      u.searchParams.delete('page');
      if (this.wall) u.searchParams.set('wall', this.wall); else u.searchParams.delete('wall');
      if (this.narrow) u.searchParams.set('sort', 'narrow'); else u.searchParams.delete('sort');
      history.replaceState(history.state, '', u.pathname + (u.search || ''));
    }

    /* ---------- client: fits my wall ---------- */
    onWall(input) {
      this.wall = +input.value >= +input.max ? null : +input.value;
      this.paintWall(input); this.apply();
      /* load the remaining pages once, in the background, so results are complete while dragging */
      if (this.wall && this.q('[data-nt-col-more]')) this.ensureAll().then(() => this.apply());
    }
    async commitWall() { if (this.wall) { await this.ensureAll(); this.apply(); } this.syncUrl(); }
    setWall(v) {
      this.wall = v;
      const input = this.q('[data-nt-col-wallinput]');
      if (input) { input.value = v || input.max; this.paintWall(input); }
      this.apply(); this.syncUrl();
    }

    paintWall(input) {
      const val = this.q('[data-nt-col-wallval]');
      const hint = this.q('[data-nt-col-hint]');
      if (val) val.textContent = this.wall ? this.wall + ' cm' : this.dataset.labelAny;
      if (hint) hint.textContent = this.wall ? (this.dataset.hint || '').replace('[inches]', Math.round(this.wall / 2.54)).replace('[wall]', this.wall) : '';
      const pct = ((+input.value - +input.min) / (+input.max - +input.min)) * 100;
      input.style.setProperty('--nt-fill', pct + '%');
    }

    apply() {
      const grid = this.q('[data-nt-col-grid]');
      const items = grid ? [...grid.children] : [];
      const room = +this.dataset.room || 20;
      let visible = 0;
      items.forEach((li) => {
        const card = li.querySelector('[data-nt-card]');
        const w = card ? parseFloat(card.dataset.ntWidth) : NaN;
        const label = card && card.querySelector('[data-nt-card-fit]');
        let show = true;
        if (this.wall && !isNaN(w)) show = w <= this.wall;
        li.hidden = !show;
        if (show) visible++;
        if (label) {
          if (this.wall && !isNaN(w) && show) {
            const roomy = this.wall - w >= room;
            label.textContent = roomy ? this.dataset.labelRoom : this.dataset.labelJust;
            label.classList.toggle('nt-card__fit--tight', !roomy);
            label.hidden = false;
          } else { label.hidden = true; }
        }
      });
      if (grid && this.narrow) {
        const width = (li) => { const c = li.querySelector('[data-nt-card]'); const v = c ? parseFloat(c.dataset.ntWidth) : NaN; return isNaN(v) ? 1e9 : v; };
        items.slice().sort((a, b) => width(a) - width(b)).forEach((li) => grid.appendChild(li));
      }
      this.paintCounts(visible, items.length);
      this.paintPills();
    }

    paintCounts(visible, loaded) {
      const total = +this.dataset.total || loaded;
      const filtered = !!this.wall;
      const shown = this.q('[data-nt-col-showing]');
      const seen = this.q('[data-nt-col-seen]');
      const count = filtered ? visible : loaded;
      const of = filtered ? loaded : total;
      if (shown) shown.innerHTML = (this.dataset.showing || '').replace('[count]', '<strong>' + count + '</strong>').replace('[total]', of);
      if (seen) seen.textContent = (this.dataset.seen || '').replace('[count]', count).replace('[total]', of);
      const empty = this.q('[data-nt-col-empty]');
      if (empty && loaded > 0) {
        empty.hidden = visible > 0;
        const t = this.q('[data-nt-col-empty-title]');
        if (t && visible === 0 && this.wall) t.textContent = (t.dataset.wallTitle || '').replace('[wall]', this.wall);
      }
      const badge = this.q('[data-nt-col-fbadge]');
      if (badge) {
        const n = this.querySelectorAll('.nt-col__pill:not([data-nt-col-wallpill])').length + (this.wall ? 1 : 0);
        badge.textContent = n; badge.hidden = n === 0;
      }
      this.querySelectorAll('[data-nt-col-clear]').forEach((c) => {
        if (c.closest('[data-nt-col-empty]')) return;
        const serverActive = this.q('.nt-col__pill:not([data-nt-col-wallpill])');
        c.hidden = !(serverActive || this.wall);
      });
    }

    paintPills() {
      const box = this.q('[data-nt-col-pills]');
      if (!box) return;
      const old = box.querySelector('[data-nt-col-wallpill]');
      if (old) old.remove();
      if (this.wall) {
        const a = document.createElement('button');
        a.type = 'button'; a.className = 'nt-col__pill'; a.setAttribute('data-nt-col-wallpill', '');
        a.innerHTML = (this.dataset.pill || '[wall] cm').replace('[wall]', this.wall) + '<span aria-hidden="true">×</span>';
        box.appendChild(a);
      }
    }
  }

  customElements.define('nt-collection', NtCollection);
})();
